// The search box on /artists and /collections.
//
// Both pages are long categorised directories with no way to find one name. The field under
// the cover replaces the whole listing with "Search results" while there is a query in it,
// and restores the listing when it is cleared.
//
// The results are rendered here rather than server-side because the page is not reloaded —
// the listing is still in the DOM, hidden, so clearing the box is instant and costs no
// request. Which means this module builds markup, and it builds it to match the tiles the
// SSR pages emit exactly: `.artists-feature-item` and `.image-big-allcoll`. Those class
// names are the contract; if either page restyles its tiles, these strings move with them.
import { api } from "../api";
import { escapeHtml } from "../utils/escapeHtml";
import { getDomElement } from "../utils/dom";
import { registerActions } from "../actionRegistry";
import { IMG_ROOT } from "../lib/constants";

/** Debounce: a backend search fires this long after the last keystroke. */
const TYPING_DELAY_MS = 500;

/** Matches `MIN_QUERY_LENGTH` in views/data/directorySearch.ts. */
const MIN_QUERY_LENGTH = 2;

/** The input, and the two containers it swaps between. */
const INPUT_ID = "directory-search";
const RESULTS_ID = "directory-search-results";
/** Everything the search hides — the regular listing, and on /artists its tab bar too. */
const HIDDEN_CLASS = "directory-search-hidden";

let timer: ReturnType<typeof setTimeout> | null = null;
/** Bumped per request; a slow reply for an older query must not paint over a newer one. */
let generation = 0;

type Mode = "artists" | "collections";

/** Which page this is, from the results container the SSR page emitted. */
function modeOf(results: HTMLElement): Mode {
  return results.dataset.mode === "collections" ? "collections" : "artists";
}

/** The sections the search replaces. `data-directory-hide` marks them in the SSR markup. */
function hideableSections(): HTMLElement[] {
  return [...document.querySelectorAll<HTMLElement>("[data-directory-hide]")];
}

function showListing(results: HTMLElement): void {
  for (const el of hideableSections()) el.classList.remove(HIDDEN_CLASS);
  results.classList.add(HIDDEN_CLASS);
  results.innerHTML = "";
}

function showResults(results: HTMLElement): void {
  for (const el of hideableSections()) el.classList.add(HIDDEN_CLASS);
  results.classList.remove(HIDDEN_CLASS);
}

/**
 * One artist tile — the markup of `FeatureSimpleItem` in `views/pages/Artists.tsx`.
 *
 * `cover || profilepic` is the page's own fallback order, and the server already resolved a
 * best-selling artwork into `profilepic` for artists who have neither, so an empty string
 * here means there is genuinely no image and the grey circle is correct.
 */
function artistTile(a: {
  username: string;
  name: string;
  cover: string;
  profilepic: string;
  artworks: number;
}): string {
  const img = a.cover || a.profilepic;
  const imgHtml = img
    ? `<div class="artists-feature-img"><img src="${escapeHtml(IMG_ROOT + img)}"></div>`
    : `<div class="artists-feature-img"><div style="width:100px;height:100px;border-radius:50%;background:#ccc;margin:0 auto;"></div></div>`;
  const s = a.artworks === 1 ? "" : "s";
  return (
    `<a href="/${escapeHtml(a.username)}"><div class="artists-feature-item">` +
    imgHtml +
    `<div class="artists-feature-name">${escapeHtml(a.name)}</div>` +
    `<div class="artists-feature-stats">${String(a.artworks)} artwork${s}</div>` +
    `</div></a>`
  );
}

/** One collection tile — the markup of `AllCollTile` in `views/pages/Collections.tsx`. */
function collectionTile(c: {
  name: string;
  href: string;
  thumbUrl: string;
  photos: number;
  isNew: boolean;
}): string {
  const badge = c.isNew ? `<span class="collections-page-sidebar-collection-new">NEW</span>` : "";
  return (
    `<a href="${escapeHtml(c.href)}"><div class="image-big-allcoll">` +
    `<div class="image-big-thumb-allcoll" style="background-image: url('${escapeHtml(c.thumbUrl)}');">&nbsp;</div>` +
    `<div class="image-big-header-allcoll">${escapeHtml(c.name)}${badge}</div>` +
    `<div class="image-big-header-allcoll-gradient">&nbsp;</div>` +
    `<div class="image-big-subheader-allcoll">${String(c.photos)} artworks</div>` +
    `</div></a>`
  );
}

/** The "Search results" block: the page's own header style, then the page's own grid. */
function resultsHtml(mode: Mode, tiles: string[], truncated: boolean): string {
  const headerClass = "collections-all-header";
  const gridClass = mode === "artists" ? "artists-feature-items" : "collections-all-images";
  if (tiles.length === 0) {
    return (
      `<div class="${headerClass}">Search results</div>` +
      `<div class="directory-search-empty">Nothing matched that search.</div>`
    );
  }
  const more = truncated ? ` <span class="directory-search-more">showing the first 60</span>` : "";
  return (
    `<div class="${headerClass}">Search results${more}</div>` +
    `<div class="${gridClass}">${tiles.join("")}</div>`
  );
}

async function runSearch(results: HTMLElement, q: string, mine: number): Promise<void> {
  const mode = modeOf(results);
  try {
    const page =
      mode === "artists"
        ? await api.search.directoryArtists({ q })
        : await api.search.directoryCollections({ q });
    if (mine !== generation) return;
    const tiles =
      mode === "artists"
        ? page.items.map((i) => artistTile(i as Parameters<typeof artistTile>[0]))
        : page.items.map((i) => collectionTile(i as Parameters<typeof collectionTile>[0]));
    showResults(results);
    results.innerHTML = resultsHtml(mode, tiles, page.truncated);
  } catch {
    if (mine !== generation) return;
    // The listing is still in the DOM and correct, so a failed search says so and leaves
    // the page usable rather than replacing it with an error.
    showResults(results);
    results.innerHTML =
      `<div class="collections-all-header">Search results</div>` +
      `<div class="directory-search-empty">Search is unavailable right now.</div>`;
  }
}

/**
 * Keyup on the field. Registered as `directorysearchtyped`.
 *
 * The debounce is the point: a backend search per keystroke would issue one query per letter
 * of every name anyone types. Clearing the box restores the listing IMMEDIATELY rather than
 * after the delay — waiting half a second to undo something is the one case where the timer
 * is only in the way.
 */
export function directorysearchtyped(): void {
  const input = getDomElement<HTMLInputElement>(INPUT_ID);
  const results = getDomElement(RESULTS_ID);
  if (!input || !results) return;

  if (timer) clearTimeout(timer);
  const q = input.value.trim();
  generation++;

  if (q.length < MIN_QUERY_LENGTH) {
    showListing(results);
    return;
  }

  const mine = generation;
  timer = setTimeout(() => {
    void runSearch(results, q, mine);
  }, TYPING_DELAY_MS);
}

registerActions({ directorysearchtyped });
